import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import {
  fetchRemoteConnections,
  REMOTE_CONNECTIONS_PAGE_SIZE,
  type RemoteConnectionFilters,
} from "../../../api/remoteConnection";
import {
  ENUM_REMOTE_CONNECTION_KIND,
  ENUM_REMOTE_CONNECTION_STATUS,
  enumremoteconnectionkindToI18nKey,
  enumremoteconnectionstatusToI18nKey,
  type EnumRemoteConnectionKind,
  type EnumRemoteConnectionStatus,
} from "../../../generated/enums";
import type { RemoteConnectionRow } from "../../../generated/types";
import { sectionToIcon } from "../../type/default";
import { RemoteConnectionStatusBadge } from "./RemoteConnectionStatusBadge";
import {
  ActiveFilterChips,
  type ActiveFilterChip,
} from "../../common/ActiveFilterChips";
import {
  DataTable,
  DataTableColumn,
  DataTableRow,
  DataTableSkeletonRows,
} from "../../common/DataTable";
import { EmptyState } from "../../common/EmptyState";
import {
  FILTER_BUTTON_PRIMARY,
  FILTER_BUTTON_SECONDARY,
  FILTER_CONTROL,
  FILTER_LABEL,
  FilterSelect,
} from "../../common/FilterControls";
import { EntityCard, EntityCardSkeletons } from "../../common/EntityCard";
import { FilterableValue } from "../../common/FilterableValue";
import { Pagination } from "../../common/Pagination";
import { HistorySearchInput } from "../../common/HistorySearchInput";

const EMPTY_FILTERS: RemoteConnectionFilters = {
  q: "",
  uuid: "",
  status: "",
  kind: "",
  idAccount: undefined,
  idCompany: undefined,
  deviceName: "",
  dateFrom: "",
  dateTo: "",
};

/** Date + heure : sans l'heure, on ne distingue pas deux connexions du même jour. */
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

/**
 * Écran « Historique » (admin/PSC) : qui s'est connecté à distance, sur quel produit,
 * quand, avec quelle issue.
 *
 * Conçu comme un OUTIL DE RECHERCHE, pas un journal à parcourir : à ~500 connexions/jour
 * et 6 mois de rétention la table atteint ~90 000 lignes. D'où la **pagination serveur**
 * (la première du projet) et des filtres qui portent sur la requête, jamais sur un tableau
 * déjà chargé.
 */
export const RemoteConnectionsTable = () => {
  const { t } = useTranslation();

  // `applied` = ce qui est envoyé au serveur ; `draft` = ce que l'utilisateur saisit.
  // Séparer les deux évite une requête par frappe dans le champ uuid.
  const [draft, setDraft] = useState<RemoteConnectionFilters>(EMPTY_FILTERS);
  const [applied, setApplied] =
    useState<RemoteConnectionFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<RemoteConnectionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filtres repliés par défaut sous `md` : cinq champs empilés pousseraient les résultats
  // hors écran au chargement.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { page: result, error: err } = await fetchRemoteConnections(applied, page);
    setRows(result.rows);
    setTotal(result.total);
    setError(err);
    setLoading(false);
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilterCount = useMemo(
    () => Object.values(applied).filter((v) => v !== "" && v !== undefined).length,
    [applied]
  );

  /**
   * Filtre posé par un CLIC dans le tableau : applique tout de suite, sans « Rechercher ».
   * `draft` suit, sinon la barre de filtres écraserait l'état à la prochaine validation.
   */
  const filterBy = (patch: Partial<RemoteConnectionFilters>) => {
    setPage(0);
    setDraft((prev) => ({ ...prev, ...patch }));
    setApplied((prev) => ({ ...prev, ...patch }));
  };

  /** Clic sur une date = le JOUR entier (la colonne stocke l'heure à la seconde). */
  const filterByDay = (iso: string) => {
    const day = iso.slice(0, 10);
    filterBy({ dateFrom: day, dateTo: day });
  };

  /** Maj+clic : étend la plage en gardant la borne la plus ancienne déjà posée. */
  const extendToDay = (iso: string) => {
    const day = iso.slice(0, 10);
    const start = applied.dateFrom || day;
    const [from, to] = day < start ? [day, start] : [start, day];
    filterBy({ dateFrom: from, dateTo: to });
  };

  /** Puces des filtres actifs : le LIBELLÉ, jamais l'id envoyé au serveur. */
  const chips: ActiveFilterChip[] = [];
  if (applied.q) {
    chips.push({
      key: "q",
      field: t("filter-search"),
      value: applied.q,
      onRemove: () => filterBy({ q: "" }),
    });
  }
  if (applied.idAccount) {
    const label = rows.find(
      (r) => r.id_account === applied.idAccount
    )?.account_name;
    chips.push({
      key: "id_account",
      field: t("history-account"),
      value: label || `#${applied.idAccount}`,
      onRemove: () => filterBy({ idAccount: undefined }),
    });
  }
  if (applied.idCompany) {
    const label = rows.find(
      (r) => r.id_company === applied.idCompany
    )?.company_name;
    chips.push({
      key: "id_company",
      field: t("company"),
      value: label || `#${applied.idCompany}`,
      onRemove: () => filterBy({ idCompany: undefined }),
    });
  }
  if (applied.uuid) {
    chips.push({
      key: "uuid",
      field: t("device-uuid"),
      value: applied.uuid,
      onRemove: () => filterBy({ uuid: "" }),
    });
  }
  if (applied.deviceName) {
    chips.push({
      key: "device_name",
      field: t("history-device"),
      value: applied.deviceName,
      onRemove: () => filterBy({ deviceName: "" }),
    });
  }
  if (applied.status) {
    chips.push({
      key: "status",
      field: t("device-status"),
      value: String(t(enumremoteconnectionstatusToI18nKey[applied.status])),
      onRemove: () => filterBy({ status: "" }),
    });
  }
  if (applied.kind) {
    chips.push({
      key: "kind",
      field: t("device-type"),
      value: String(t(enumremoteconnectionkindToI18nKey[applied.kind])),
      onRemove: () => filterBy({ kind: "" }),
    });
  }
  if (applied.dateFrom || applied.dateTo) {
    const short = (d?: string) =>
      d ? d.split("-").reverse().slice(0, 2).join("/") : "…";
    const value =
      applied.dateFrom === applied.dateTo
        ? short(applied.dateFrom)
        : `${short(applied.dateFrom)} → ${short(applied.dateTo)}`;
    chips.push({
      key: "date",
      field: t("history-date"),
      value,
      onRemove: () => filterBy({ dateFrom: "", dateTo: "" }),
    });
  }

  const submitFilters = () => {
    setPage(0); // un nouveau filtre repart de la première page, sinon on peut tomber hors bornes
    setApplied(draft);
  };

  const resetFilters = () => {
    setPage(0);
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const scrollKey = `${page}|${JSON.stringify(applied)}`;

  const columns: DataTableColumn<
    "date" | "account" | "device" | "kind" | "status"
  >[] = [
    {
      key: "date",
      header: t("history-date"),
      className: "w-2/12 whitespace-nowrap",
    },
    { key: "account", header: t("history-account"), className: "w-3/12" },
    { key: "device", header: t("history-device"), className: "w-3/12" },
    { key: "kind", header: t("device-type"), className: "w-2/12" },
    { key: "status", header: t("device-status"), className: "w-2/12" },
  ];

  return (
    // Pas de `Header` ni de padding ici : HistoryPage rend le titre une seule fois
    // au-dessus des deux onglets.
    <div>
      {/* Barre de filtres — empilée en mobile, repliable sous `md`. */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className="md:hidden flex items-center gap-2 text-default-text font-medium"
        >
          <FontAwesomeIcon icon="filter" />
          {t("filter-by")}
          {/* Sans ce compteur, un utilisateur oublie qu'il filtre et croit la liste vide. */}
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-secondary px-2 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div
          className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:flex-wrap md:items-end gap-3 mt-3 md:mt-0`}
        >
          <HistorySearchInput
            label={t("filter-search")}
            value={draft.q ?? ""}
            onChange={(value) => setDraft({ ...draft, q: value })}
            onSubmit={submitFilters}
            // i18n.t("history-search-placeholder-remote")
            placeholder={t("history-search-placeholder-remote")}
          />

          <label className={FILTER_LABEL}>
            {t("device-status")}
            <FilterSelect
              value={draft.status ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: e.target.value as EnumRemoteConnectionStatus | "",
                })
              }
            >
              <option value="">{t("history-all")}</option>
              {ENUM_REMOTE_CONNECTION_STATUS.map((s) => (
                <option key={s} value={s}>
                  {t(enumremoteconnectionstatusToI18nKey[s])}
                </option>
              ))}
            </FilterSelect>
          </label>

          <label className={FILTER_LABEL}>
            {t("device-type")}
            <FilterSelect
              value={draft.kind ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  kind: e.target.value as EnumRemoteConnectionKind | "",
                })
              }
            >
              <option value="">{t("history-all")}</option>
              {ENUM_REMOTE_CONNECTION_KIND.map((k) => (
                <option key={k} value={k}>
                  {t(enumremoteconnectionkindToI18nKey[k])}
                </option>
              ))}
            </FilterSelect>
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-from")}
            <input
              type="date"
              value={draft.dateFrom ?? ""}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-to")}
            <input
              type="date"
              value={draft.dateTo ?? ""}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitFilters}
              className={FILTER_BUTTON_PRIMARY}
            >
              {t("filter-search")}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className={FILTER_BUTTON_SECONDARY}
            >
              {t("history-reset")}
            </button>
          </div>
        </div>
      </div>

      {/* Au-dessus de la zone de résultats : quand un filtre ne renvoie rien, c'est
          précisément là qu'il faut voir ce qui filtre — et pouvoir le retirer. */}
      <ActiveFilterChips chips={chips} onClearAll={resetFilters} />

      {error ? (
        <p className="mt-10 text-center text-red-700">{t("error")}</p>
      ) : loading ? (
        <>
          <div className="md:hidden mt-6">
            <EntityCardSkeletons rows={4} />
          </div>
          <div className="hidden md:block mt-6">
            <DataTable columns={columns} scrollKey={scrollKey}>
              <DataTableSkeletonRows columns={columns} />
            </DataTable>
          </div>
        </>
      ) : rows.length === 0 ? (
        // Deux situations très différentes pour l'utilisateur : rien à voir, ou un filtre
        // trop restrictif. Le même message pour les deux laisserait croire à un bug.
        <EmptyState
          icon={sectionToIcon.section_history}
          title={
            activeFilterCount > 0
              ? t("table-no-result")
              : t("history-empty-remote")
          }
        />
      ) : (
        <>
          {/* MOBILE : une carte par connexion, au style des cartes Produits. Mêmes filtres
              que le tableau : ajouter un critère à l'un sans l'autre est l'oubli classique. */}
          <div className="md:hidden space-y-4 mt-6">
            {rows.map((row, i) => (
              <EntityCard
                key={`${row.date}-${row.id_account}-${i}`}
                badge={
                  <FilterableValue
                    variant="badge"
                    label={<RemoteConnectionStatusBadge status={row.status} />}
                    active={applied.status === row.status}
                    onFilter={() => filterBy({ status: row.status })}
                    title={t("filter-title-status")}
                  />
                }
                title={
                  row.account_name.trim() === "" ? (
                    "-"
                  ) : (
                    <FilterableValue
                      label={row.account_name}
                      active={applied.idAccount === row.id_account}
                      onFilter={() => filterBy({ idAccount: row.id_account })}
                      title={t("filter-title-user")}
                    />
                  )
                }
                rows={[
                  {
                    label: t("history-date"),
                    value: (
                      <FilterableValue
                        label={formatDate(row.date)}
                        active={
                          applied.dateFrom === row.date.slice(0, 10) &&
                          applied.dateTo === row.date.slice(0, 10)
                        }
                        onFilter={() => filterByDay(row.date)}
                        title={t("filter-title-day")}
                      />
                    ),
                  },
                  {
                    label: t("company"),
                    value:
                      row.company_name.trim() === "" ? (
                        "-"
                      ) : (
                        <FilterableValue
                          label={row.company_name}
                          active={applied.idCompany === row.id_company}
                          onFilter={() =>
                            filterBy({ idCompany: row.id_company })
                          }
                          title={t("filter-title-company")}
                        />
                      ),
                  },
                  {
                    label: t("history-device"),
                    value:
                      row.device_name.trim() === "" ? (
                        "-"
                      ) : (
                        <FilterableValue
                          label={row.device_name}
                          active={applied.deviceName === row.device_name}
                          onFilter={() =>
                            filterBy({ deviceName: row.device_name })
                          }
                          title={t("filter-title-device")}
                        />
                      ),
                  },
                  {
                    label: t("device-type"),
                    value: (
                      <FilterableValue
                        label={t(enumremoteconnectionkindToI18nKey[row.kind])}
                        active={applied.kind === row.kind}
                        onFilter={() => filterBy({ kind: row.kind })}
                        title={t("filter-title-channel")}
                      />
                    ),
                  },
                ]}
              />
            ))}
          </div>

          {/* DESKTOP : le tableau complet, uuid compris. */}
          <div className="hidden md:block mt-6 overflow-x-auto">
            <DataTable columns={columns} scrollKey={scrollKey}>
              {rows.map((row, i) => (
                <DataTableRow
                  key={`${row.date}-${row.id_account}-${i}`}
                  columns={columns}
                  cells={{
                    date: (
                      <>
                        {/* Clic = ce jour ; Maj+clic = étendre la plage jusqu'à ce jour. */}
                        <FilterableValue
                          label={formatDate(row.date)}
                          active={
                            applied.dateFrom === row.date.slice(0, 10) &&
                            applied.dateTo === row.date.slice(0, 10)
                          }
                          onFilter={() => filterByDay(row.date)}
                          onRangeSelect={() => extendToDay(row.date)}
                          title={t("filter-title-day-range")}
                        />
                      </>
                    ),
                    account: (
                      <>
                        <span className="text-default-text">
                          {row.account_name.trim() === "" ? (
                            "-"
                          ) : (
                            <FilterableValue
                              label={row.account_name}
                              active={applied.idAccount === row.id_account}
                              onFilter={() =>
                                filterBy({ idAccount: row.id_account })
                              }
                              title={t("filter-title-user")}
                            />
                          )}
                        </span>
                        <span className="block text-xs text-grey-readable">
                          {row.company_name.trim() === "" ? (
                            "-"
                          ) : (
                            <FilterableValue
                              label={row.company_name}
                              active={applied.idCompany === row.id_company}
                              onFilter={() =>
                                filterBy({ idCompany: row.id_company })
                              }
                              title={t("filter-title-company")}
                            />
                          )}
                        </span>
                      </>
                    ),
                    device: (
                      <>
                        <span className="text-default-text">
                          {row.device_name.trim() === "" ? (
                            "-"
                          ) : (
                            <FilterableValue
                              label={row.device_name}
                              active={applied.deviceName === row.device_name}
                              onFilter={() =>
                                filterBy({ deviceName: row.device_name })
                              }
                              title={t("filter-title-device")}
                            />
                          )}
                        </span>
                        <span className="block text-xs text-grey-readable">
                          <FilterableValue
                            label={row.uuid}
                            active={applied.uuid === row.uuid}
                            onFilter={() => filterBy({ uuid: row.uuid })}
                            title={t("filter-title-uuid")}
                          />
                        </span>
                      </>
                    ),
                    kind: (
                      <FilterableValue
                        label={t(enumremoteconnectionkindToI18nKey[row.kind])}
                        active={applied.kind === row.kind}
                        onFilter={() => filterBy({ kind: row.kind })}
                        title={t("filter-title-channel")}
                      />
                    ),
                    status: (
                      <FilterableValue
                        variant="badge"
                        label={
                          <RemoteConnectionStatusBadge status={row.status} />
                        }
                        active={applied.status === row.status}
                        onFilter={() => filterBy({ status: row.status })}
                        title={t("filter-title-status")}
                      />
                    ),
                  }}
                />
              ))}
            </DataTable>
          </div>

          <Pagination
            page={page}
            pageSize={REMOTE_CONNECTIONS_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};
